// components/Notice.js - 연락처 정보 제거 버전
import React, { useState, useEffect } from 'react';
import { X, Bell, AlertCircle, Info, CheckCircle, Calendar } from 'lucide-react';
import { db } from '../firebase';

const Notice = ({ onClose }) => {
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadNotices();
    }, []);

    // 공지사항을 닫을 때 확인 시간 업데이트
    const handleClose = () => {
        localStorage.setItem('lastNoticeChecked', new Date().toISOString());
        onClose();
    };

    const loadNotices = async () => {
        try {
            console.log('공지사항 로드 시작...');
            const { collection, getDocs } = await import('firebase/firestore');

            // 단순 쿼리로 변경 (인덱스 불필요)
            const snapshot = await getDocs(collection(db, 'notices'));
            const noticesList = [];

            snapshot.forEach(doc => {
                const data = doc.data();
                console.log('로드된 공지사항:', { id: doc.id, ...data });

                // 활성화된 공지사항만 필터링
                if (data.isActive === true) {
                    noticesList.push({ id: doc.id, ...data });
                }
            });

            // 클라이언트에서 정렬
            noticesList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            console.log('필터링된 공지사항 수:', noticesList.length);
            setNotices(noticesList);
        } catch (error) {
            console.error('공지사항 로드 실패:', error);

            // Firebase 연결 실패 시 기본 공지사항 표시
            setNotices([
                {
                    id: 'default',
                    title: '서비스 이용 안내',
                    content: 'FC퍼널 서비스를 이용해주셔서 감사합니다.',
                    type: 'info',
                    createdAt: new Date().toISOString()
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const getNoticeIcon = (type) => {
        switch (type) {
            case 'urgent':
                return <AlertCircle className="w-5 h-5 text-red-500" />;
            case 'info':
                return <Info className="w-5 h-5 text-blue-500" />;
            case 'update':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            default:
                return <Bell className="w-5 h-5 text-gray-500" />;
        }
    };

    const getNoticeStyle = (type) => {
        switch (type) {
            case 'urgent':
                return 'border-red-200 bg-red-50';
            case 'info':
                return 'border-blue-200 bg-blue-50';
            case 'update':
                return 'border-green-200 bg-green-50';
            default:
                return 'border-gray-200 bg-gray-50';
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'urgent': return '긴급';
            case 'info': return '정보';
            case 'update': return '업데이트';
            default: return '일반';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('ko-KR');
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl border border-white/20">
                {/* 헤더 */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                                <Bell className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">공지사항</h2>
                                <p className="text-gray-600 mt-1">중요한 안내사항을 확인하세요</p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-300"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* 공지사항 목록 */}
                <div className="p-6 overflow-y-auto max-h-96">
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-gray-500">공지사항을 불러오는 중...</p>
                        </div>
                    ) : notices.length === 0 ? (
                        <div className="text-center py-16">
                            <Bell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500 text-xl font-medium">등록된 공지사항이 없습니다</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {notices.map((notice) => (
                                <div
                                    key={notice.id}
                                    className={`border-2 rounded-2xl p-5 transition-all duration-300 ${getNoticeStyle(notice.type)}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 mt-1">
                                            {getNoticeIcon(notice.type)}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h3 className="font-bold text-gray-800 text-lg">
                                                    {notice.title}
                                                </h3>
                                                <span className={`text-xs px-2 py-1 rounded-full font-bold ${notice.type === 'urgent' ? 'bg-red-500 text-white' :
                                                    notice.type === 'info' ? 'bg-blue-500 text-white' :
                                                        notice.type === 'update' ? 'bg-green-500 text-white' :
                                                            'bg-gray-500 text-white'
                                                    }`}>
                                                    {getTypeLabel(notice.type)}
                                                </span>
                                            </div>
                                            <p className="text-gray-700 leading-relaxed mb-3 whitespace-pre-line">
                                                {notice.content}
                                            </p>
                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                <Calendar className="w-4 h-4" />
                                                <span>{formatDate(notice.createdAt)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* 푸터 - 연락처 정보 제거 */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                    <div className="flex justify-end">
                        <button
                            onClick={handleClose}
                            className="bg-blue-500 text-white px-6 py-2 rounded-xl font-bold hover:bg-blue-600 transition-all duration-300"
                        >
                            확인
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Notice;